/**
 * IndexedDB persistence for the query cache, one entry per signed-in user
 * (`caseblink-rq:{uid}`), so a later sign-in never restores someone else's
 * data. Only queries tagged `meta: { persist: true }` are written.
 */
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { Query } from "@tanstack/react-query";
import type { PersistedClient } from "@tanstack/react-query-persist-client";
import { del, get, set } from "idb-keyval";

import { DAY_MS } from "./queryClient";

export const PERSIST_MAX_AGE_MS = DAY_MS;
export const PERSIST_THROTTLE_MS = 1000;

export const persistKey = (uid: string): string => `caseblink-rq:${uid}`;

const hasIndexedDb = (): boolean =>
  typeof indexedDB !== "undefined" && indexedDB !== null;

// Without IndexedDB (jsdom, some private modes) nothing is saved; reads miss.
const storage = {
  getItem: async (key: string): Promise<string | null> =>
    hasIndexedDb() ? (await get<string>(key)) ?? null : null,
  setItem: async (key: string, value: string): Promise<void> => {
    if (hasIndexedDb()) await set(key, value);
  },
  removeItem: async (key: string): Promise<void> => {
    if (hasIndexedDb()) await del(key);
  }
};

type InfiniteData = { pages: unknown[]; pageParams: unknown[] };

const isInfiniteData = (data: unknown): data is InfiniteData =>
  typeof data === "object" &&
  data !== null &&
  Array.isArray((data as InfiniteData).pages) &&
  Array.isArray((data as InfiniteData).pageParams);

/**
 * Infinite queries (the activity feed) keep only their first page on disk:
 * that is what a revisit shows first, and later pages are cheap to re-page.
 */
export const trimForPersist = (client: PersistedClient): PersistedClient => ({
  ...client,
  clientState: {
    ...client.clientState,
    queries: client.clientState.queries.map((q) => {
      const { data } = q.state as { data: unknown };
      if (!isInfiniteData(data) || data.pages.length <= 1) return q;
      return {
        ...q,
        state: {
          ...q.state,
          data: {
            pages: data.pages.slice(0, 1),
            pageParams: data.pageParams.slice(0, 1)
          }
        }
      };
    })
  }
});

export const createPersister = (uid: string) =>
  createAsyncStoragePersister({
    storage,
    key: persistKey(uid),
    throttleTime: PERSIST_THROTTLE_MS,
    serialize: (client) => JSON.stringify(trimForPersist(client))
  });

/** Only opted-in, successful queries make it to disk. */
export const shouldPersistQuery = (query: Query): boolean =>
  query.meta?.persist === true && query.state.status === "success";

/** Forget the user's saved cache (sign-out). */
export const clearPersistedQueries = (uid: string | null | undefined) =>
  uid ? storage.removeItem(persistKey(uid)) : Promise.resolve();
